import React from 'react';
import { Link } from 'react-router-dom';
import './PolicyPages.css';

const TermsOfServicePage: React.FC = () => {
    return (
        <div className="policy-page">
            <header>
                <Link to="/" className="back-link">← Back to Home</Link>
                <h1>Terms of Service</h1>
                <p>Last updated: May 27, 2025</p>
            </header>

            <div className="policy-content">
                <section>
                    <h2>1. Acceptance of Terms</h2>
                    <p>
                        By accessing and using MailMeld ("the Service," "we," "us," or "our"), you ("User" or "you") agree to be bound by these Terms of Service
                        ("Terms") and all applicable laws and regulations. If you do not agree with any of these terms, you are
                        prohibited from using the Service. These Terms constitute a legally binding agreement between you and RVD Tech Solutions.
                    </p>
                    <p>
                        <strong>You must be at least 18 years old or have parental consent to use this Service.</strong>
                    </p>
                </section>

                <section>
                    <h2>2. Description of Service</h2>
                    <p>
                        MailMeld is a Google Workspace Add-on that provides email summarization and organization services.
                        The Service processes email metadata (but not email content) to create daily summaries and categorized views of your inbox.
                    </p>
                    <p><strong>Key Features:</strong></p>
                    <ul>
                        <li>Daily email summaries based on metadata analysis</li>
                        <li>Email categorization and organization</li>
                        <li>Priority inbox management</li>
                        <li>Integration with Google Workspace</li>
                    </ul>
                    <p>
                        <strong>Service Availability:</strong> We strive to maintain 99.9% uptime but do not guarantee uninterrupted service.
                        Scheduled maintenance will be announced in advance when possible.
                    </p>
                </section>

                <section>
                    <h2>3. User Account and Registration</h2>
                    <p>To use MailMeld, you must:</p>
                    <ul>
                        <li>Have a valid Google Workspace or Gmail account</li>
                        <li>Complete the OAuth authorization process</li>
                        <li>Provide accurate and complete information</li>
                        <li>Maintain the security and confidentiality of your account credentials</li>
                    </ul>
                    <p>
                        You are responsible for all activities that occur under your account. Notify us immediately of any unauthorized use.
                    </p>
                </section>

                <section>
                    <h2>4. User Responsibilities and Acceptable Use</h2>
                    <p>As a user of MailMeld, you agree to:</p>
                    <ul>
                        <li>Provide accurate account information and keep it updated</li>
                        <li>Maintain the security of your account and credentials</li>
                        <li>Use the Service in compliance with all applicable laws and regulations</li>
                        <li>Not misuse, reverse engineer, or attempt to circumvent the Service</li>
                        <li>Not use the Service for any illegal, harmful, or unauthorized purpose</li>
                        <li>Not interfere with or disrupt the Service or its servers</li>
                        <li>Not attempt to gain unauthorized access to other users' data</li>
                        <li>Not use automated scripts or bots without explicit permission</li>
                    </ul>
                    
                    <h3>Prohibited Uses</h3>
                    <p>You may not use MailMeld to:</p>
                    <ul>
                        <li>Violate any laws or regulations</li>
                        <li>Infringe on intellectual property rights</li>
                        <li>Transmit malware, viruses, or harmful code</li>
                        <li>Engage in spamming or unsolicited communications</li>
                        <li>Circumvent security measures or access controls</li>
                    </ul>
                </section>

                <section>
                    <h2>5. Google API Services and Third-Party Integration</h2>
                    <p>
                        MailMeld integrates with Google APIs and services. Your use of MailMeld is also subject to:
                    </p>
                    <ul>
                        <li><a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Google Terms of Service</a></li>
                        <li><a href="https://developers.google.com/terms/api-services-user-data-policy" target="_blank" rel="noopener noreferrer">Google API Services User Data Policy</a></li>
                        <li>Google Workspace Marketplace Terms (if applicable)</li>
                    </ul>
                    <p>
                        We are not responsible for changes to Google's APIs or policies that may affect the Service.
                    </p>
                </section>

                <section>
                    <h2>6. Data Processing and Privacy</h2>
                    <p>
                        Your use of MailMeld is governed by our{' '}
                        <Link to="/privacy">Privacy Policy</Link>, which is incorporated into these Terms by reference.
                    </p>
                    <p><strong>Key Points:</strong></p>
                    <ul>
                        <li>We process email metadata only, not email content</li>
                        <li>Data is encrypted in transit and at rest</li>
                        <li>You retain ownership of your data</li>
                        <li>You can request data deletion at any time</li>
                    </ul>
                </section>

                <section>
                    <h2>7. Intellectual Property Rights</h2>
                    <p>
                        <strong>Our Rights:</strong> All content, features, functionality, software, and technology of the Service are owned by RVD Tech Solutions
                        and are protected by international copyright, trademark, patent, and other intellectual property laws.
                    </p>
                    <p>
                        <strong>Your Rights:</strong> You retain all rights to your data and content. We claim no ownership over your emails or personal information.
                    </p>
                    <p>
                        <strong>License Grant:</strong> We grant you a limited, non-exclusive, non-transferable license to use the Service
                        in accordance with these Terms.
                    </p>
                </section>

                <section>
                    <h2>8. Subscription and Payment Terms</h2>
                    <p>
                        <strong>Free Tier:</strong> We may offer limited free access to certain features.
                    </p>
                    <p>
                        <strong>Paid Subscriptions:</strong> Premium features may require subscription payments.
                    </p>
                    <ul>
                        <li>Subscriptions are billed in advance</li>
                        <li>Fees are non-refundable except as required by law</li>
                        <li>You may cancel your subscription at any time</li>
                        <li>Price changes will be communicated 30 days in advance</li>
                    </ul>
                </section>

                <section>
                    <h2>9. Service Modifications and Availability</h2>
                    <p>
                        We reserve the right to:
                    </p>
                    <ul>
                        <li>Modify, suspend, or discontinue any part of the Service at any time</li>
                        <li>Update features and functionality</li>
                        <li>Perform maintenance and upgrades</li>
                        <li>Change API endpoints or data structures</li>
                    </ul>
                    <p>
                        We will provide reasonable notice of significant changes when possible. Continued use of the Service
                        after modifications constitutes acceptance of the changes.
                    </p>
                </section>

                <section>
                    <h2>10. Warranties and Disclaimers</h2>
                    <p>
                        <strong>DISCLAIMER:</strong> MailMeld is provided "AS IS" and "AS AVAILABLE" without warranties of any kind,
                        either express or implied, including but not limited to implied warranties of merchantability,
                        fitness for a particular purpose, or non-infringement.
                    </p>
                    <p>
                        We do not warrant that:
                    </p>
                    <ul>
                        <li>The Service will be uninterrupted or error-free</li>
                        <li>Defects will be corrected</li>
                        <li>The Service is free of viruses or harmful components</li>
                        <li>Results obtained from the Service will be accurate or reliable</li>
                    </ul>
                </section>

                <section>
                    <h2>11. Limitation of Liability</h2>
                    <p>
                        <strong>TO THE MAXIMUM EXTENT PERMITTED BY LAW, IN NO EVENT SHALL RVD TECH SOLUTIONS BE LIABLE FOR:</strong>
                    </p>
                    <ul>
                        <li>Any indirect, incidental, special, consequential, or punitive damages</li>
                        <li>Loss of profits, data, use, goodwill, or other intangible losses</li>
                        <li>Damages resulting from unauthorized access to your account</li>
                        <li>Interruption or cessation of the Service</li>
                        <li>Any third-party content or conduct</li>
                    </ul>
                    <p>
                        <strong>Our total liability to you for all claims shall not exceed $100 or the amount you paid us in the last 12 months, whichever is greater.</strong>
                    </p>
                </section>

                <section>
                    <h2>12. Indemnification</h2>
                    <p>
                        You agree to indemnify, defend, and hold harmless RVD Tech Solutions and its affiliates from any claims,
                        damages, losses, or expenses (including reasonable attorney fees) arising from:
                    </p>
                    <ul>
                        <li>Your use of the Service</li>
                        <li>Your violation of these Terms</li>
                        <li>Your violation of any law or third-party rights</li>
                        <li>Your content or data</li>
                    </ul>
                </section>

                <section>
                    <h2>13. Account Termination</h2>
                    <p>
                        <strong>Termination by You:</strong> You may terminate your account at any time by disconnecting the add-on
                        from your Google account or contacting us.
                    </p>
                    <p>
                        <strong>Termination by Us:</strong> We may terminate or suspend your access immediately for:
                    </p>
                    <ul>
                        <li>Violation of these Terms</li>
                        <li>Illegal or harmful activity</li>
                        <li>Extended periods of inactivity</li>
                        <li>Technical or security reasons</li>
                    </ul>
                    <p>
                        Upon termination, your data will be deleted according to our Privacy Policy.
                    </p>
                </section>

                <section>
                    <h2>14. Dispute Resolution</h2>
                    <p>
                        <strong>Informal Resolution:</strong> Before filing any formal dispute, please contact us at connect@rvdtechsolutions.com
                        to attempt to resolve the issue informally.
                    </p>
                    <p>
                        <strong>Binding Arbitration:</strong> Any disputes that cannot be resolved informally shall be resolved through
                        binding arbitration in accordance with the rules of the American Arbitration Association.
                    </p>
                    <p>
                        <strong>Class Action Waiver:</strong> You agree not to participate in any class action lawsuits against us.
                    </p>
                </section>

                <section>
                    <h2>15. Governing Law and Jurisdiction</h2>
                    <p>
                        These Terms shall be governed by and construed in accordance with the laws of the United States
                        and the State of California, without regard to conflict of law provisions.
                    </p>
                    <p>
                        Any legal action must be brought in the state or federal courts located in California.
                    </p>
                </section>

                <section>
                    <h2>16. Severability and Entire Agreement</h2>
                    <p>
                        If any provision of these Terms is found to be unenforceable, the remaining provisions will remain in full force and effect.
                    </p>
                    <p>
                        These Terms, along with our Privacy Policy, constitute the entire agreement between you and RVD Tech Solutions
                        regarding the Service.
                    </p>
                </section>

                <section>
                    <h2>17. Changes to Terms</h2>
                    <p>
                        We reserve the right to modify these Terms at any time. We will notify users of material changes through:
                    </p>
                    <ul>
                        <li>Email notification</li>
                        <li>In-app notifications</li>
                        <li>Prominent notice on our website</li>
                    </ul>
                    <p>
                        Changes become effective 30 days after notification. Continued use of the Service after the effective date
                        constitutes acceptance of the updated Terms.
                    </p>
                </section>

                <section>
                    <h2>18. Contact Information</h2>
                    <p>
                        For any questions regarding these Terms, please contact us:
                    </p>
                    <div style={{ marginLeft: '20px' }}>
                        <p><strong>RVD Tech Solutions</strong></p>
                        <p>Email: connect@rvdtechsolutions.com</p>
                        <p>Legal inquiries: legal@rvdtechsolutions.com</p>
                        <p>Response Time: We aim to respond within 48 hours</p>
                    </div>
                </section>

                <section>
                    <h2>19. Effective Date</h2>
                    <p>
                        These Terms of Service are effective as of May 27, 2025, and apply to all use of MailMeld from that date forward.
                    </p>
                </section>
            </div>
        </div>
    );
};

export default TermsOfServicePage;